/**
 * Cliente HTTP hacia la API de KEORA (mismo origen: Nginx sirve /api).
 * Traduce el formato de error estándar del backend a ApiError.
 * La autorización siempre la decide el backend; el frontend solo mejora la experiencia.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown>,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ErrorBody {
  error?: { code?: string; message?: string; details?: Record<string, unknown>; requestId?: string };
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

async function request<T>(method: Method, path: string, body?: unknown, signal?: AbortSignal, file?: File, extra: Record<string, string> = {}): Promise<T> {
  let response: Response;
  const headers: Record<string, string> = file
    ? { Accept: 'application/json', 'Content-Type': file.type || 'application/octet-stream', 'X-File-Name': encodeURIComponent(file.name), ...extra }
    : { Accept: 'application/json', ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) };
  try {
    response = await fetch(`/api${path}`, {
      method,
      credentials: 'same-origin',
      headers,
      body: file ?? (body === undefined ? undefined : JSON.stringify(body)),
      signal,
    });
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error;
    throw new ApiError(0, 'NETWORK_ERROR', 'No se pudo conectar con el servidor. Revisa tu conexión.');
  }
  if (response.status === 204) return undefined as T;
  const data: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const e = (data as ErrorBody | null)?.error;
    throw new ApiError(response.status, e?.code ?? 'HTTP_ERROR', e?.message ?? `Error ${response.status}`, e?.details, e?.requestId);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>('GET', path, undefined, signal),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
  /** Sube un archivo como cuerpo de la solicitud (el servidor reconoce su tipo real). */
  upload: <T>(path: string, file: File, headers?: Record<string, string>) => request<T>('POST', path, undefined, undefined, file, headers),
  /** Reemplaza un archivo (p. ej. la foto de perfil). */
  putFile: <T>(path: string, file: File) => request<T>('PUT', path, undefined, undefined, file),
};

/** Mensaje legible para mostrar al usuario. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === 'VALIDATION_ERROR' && Array.isArray(error.details?.issues)) {
      const first = (error.details.issues as { message?: string }[])[0];
      return first?.message ? `Revisa los datos: ${first.message}` : error.message;
    }
    return error.message;
  }
  return 'Ocurrió un error inesperado.';
}
